// Browser-side check of a data file's header before it's uploaded, so a missing column is
// caught right away instead of after the upload. The required columns depend on the outcome
// data source (matching the voter-file templates the server validates against): Catalist's
// unique key is dwid + state, TargetSmart's is voterbase_id alone, and a write-in source only
// needs its condition column. The pipeline reads columns by name, so order doesn't matter,
// and names are compared ignoring case (the server normalizes case).

const HEADER_BYTES = 64 * 1024; // the header line is always well inside this

const REQUIRED_COLUMNS = {
  catalist: ['dwid', 'state', 'condition'],
  targetsmart: ['voterbase_id', 'condition'],
  other: ['condition'],
};

/** The columns a data file must have for this outcome source (none before one is chosen). */
export const requiredColumns = (source) => REQUIRED_COLUMNS[source] || [];

// Splits one CSV line into cells, honoring double-quoted cells ("a,b" and "" escapes).
const parseCsvLine = (line) => {
  const cells = [];
  let cell = '';
  let quoted = false;
  for (let i = 0; i < line.length; i += 1) {
    const char = line[i];
    if (quoted) {
      if (char === '"' && line[i + 1] === '"') {
        cell += '"';
        i += 1;
      } else if (char === '"') {
        quoted = false;
      } else {
        cell += char;
      }
    } else if (char === '"') {
      quoted = true;
    } else if (char === ',') {
      cells.push(cell);
      cell = '';
    } else {
      cell += char;
    }
  }
  cells.push(cell);
  return cells.map((c) => c.trim());
};

export const readHeader = async (file) => {
  const text = await file.slice(0, HEADER_BYTES).text();
  const firstLine = text.replace(/^﻿/, '').split(/\r?\n/)[0] || '';
  return parseCsvLine(firstLine);
};

/** Returns an error message for a file missing a required column, or '' if it's fine. */
export const checkDataFileColumns = async (file, source) => {
  const required = requiredColumns(source);
  if (required.length === 0) return '';
  const header = new Set((await readHeader(file)).map((name) => name.toLowerCase()));
  const missing = required.filter((name) => !header.has(name));
  if (missing.length === 0) return '';
  return (
    `${file.name} is missing the ${missing.join(', ')} column${missing.length > 1 ? 's' : ''}. ` +
    `Required columns: ${required.join(', ')}. Please fix the file and upload it again.`
  );
};
